import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { checkRateLimit, validatePassword } from '../../utils/security'
import { supabase } from '../../lib/supabase'
import { useOrganization, applyOrganizationBranding } from '../../lib/organizationContext'
import { useTheme } from '../../lib/theme'
import { Surface, TextField, Label, InputGroup, Checkbox, Button, Alert } from '@heroui/react'
import Mail from '~icons/solar/letter-bold'
import Lock from '~icons/solar/lock-password-bold'
import UserIcon from '~icons/solar/user-bold'
import LogIn from '~icons/solar/login-2-linear'
import Eye from '~icons/solar/eye-bold'
import EyeOff from '~icons/solar/eye-closed-bold'
import ArrowRight from '~icons/solar/arrow-right-outline'
import CheckCircle from '~icons/solar/check-circle-bold'
import AlertCircle from '~icons/solar/danger-circle-bold'
import LoadingSpinner from '~icons/svg-spinners/270-ring-with-bg'

export default function AuthPage() {
  const [searchParams] = useSearchParams()
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [signupSuccess, setSignupSuccess] = useState(false)
  const [confirmationStatus, setConfirmationStatus] = useState<'checking' | 'success' | 'error' | null>(null)
  const { data: orgData = null, isFetched: orgFetched } = useOrganization()
  const navigate = useNavigate()
  const { login, signup, isAuthenticated } = useAuth()
  const { theme } = useTheme()

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    organizationId: '',
    referredBy: ''
  })

  // Check for email confirmation
  useEffect(() => {
    const checkEmailConfirmation = async () => {
      const type = searchParams.get('type')

      if (type === 'signup' || type === 'email' || type === 'invite') {
        setConfirmationStatus('checking')

        try {
          // Supabase automatically handles the token exchange
          // We just need to check if the user is now authenticated
          const { data: { session }, error } = await supabase.auth.getSession()

          if (error) throw error

          if (session) {
            setConfirmationStatus('success')

            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
              navigate('/affiliate/dashboard')
            }, 2000)
          } else {
            // Token might be invalid or expired
            setConfirmationStatus('error')
          }
        } catch (err) {
          console.error('[Auth] Email confirmation error:', err)
          setConfirmationStatus('error')
        }
      }
    }

    checkEmailConfirmation()
  }, [searchParams, navigate])

  // Fetch organization data dynamically based on domain
  useEffect(() => {
    if (!orgFetched) return

    if (!orgData) {
      setError('No organization configured for this domain. Please contact support.')
      return
    }

    setFormData(prev => ({ ...prev, organizationId: orgData.id }))

    // Apply branding (colors, favicon, etc.) -- also drives HeroUI's
    // --accent token, so Button/Checkbox/links across the app pick up
    // this tenant's brand color automatically. getCurrentOrganization() is
    // already cached by this point (useOrganization's queryFn just
    // populated it), so this doesn't issue a second network request.
    applyOrganizationBranding()
    if (orgData.branding?.primary_color) {
      document.documentElement.style.setProperty('--accent', orgData.branding.primary_color)
    }
  }, [orgData, orgFetched])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // SECURITY: Check rate limiting to prevent brute force attacks
    const rateLimit = checkRateLimit(
      isLogin ? `login:${formData.email}` : `signup:${formData.email}`,
      5, // max 5 attempts
      15 * 60 * 1000, // within 15 minutes
      15 * 60 * 1000  // 15 minute lockout
    )

    if (!rateLimit.allowed) {
      setError(rateLimit.message || 'Too many attempts. Please try again later.')
      return
    }

    // SECURITY: Validate password strength for signup
    if (!isLogin) {
      const passwordValidation = validatePassword(formData.password)
      if (!passwordValidation.isValid) {
        setError(passwordValidation.feedback.join('. '))
        return
      }
    }

    setLoading(true)

    try {
      if (isLogin) {
        await login(formData.email, formData.password)
        navigate('/affiliate/dashboard')
      } else {
        // Redirect to application form instead of allowing direct signup
        setError('Please use the "Become an Affiliate" application form to join our program.')
        setTimeout(() => {
          navigate('/auth/apply')
        }, 2000)
        return
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const appName = orgData?.app_name || 'Affiliate Hub'
  const tagline = orgData?.branding?.tagline || 'Partner Program'

  // Show signup success message
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Surface variant="default" className="rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-accent-soft-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Check Your Email
            </h2>
            <p className="text-muted mb-4">
              We've sent a confirmation email to <strong className="text-foreground">{formData.email}</strong>
            </p>
            <Surface variant="secondary" className="rounded-xl p-4 text-sm text-left mb-6">
              <p className="font-medium text-foreground mb-2">Next steps:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted">
                <li>Check your email inbox</li>
                <li>Click the confirmation link</li>
                <li>You'll be automatically logged in</li>
              </ol>
            </Surface>
            <p className="text-sm text-muted mb-4">
              Didn't receive the email? Check your spam folder.
            </p>
            <Button
              variant="ghost"
              onPress={() => {
                setSignupSuccess(false)
                setIsLogin(true)
                setFormData({ email: '', password: '', name: '', organizationId: '', referredBy: '' })
              }}
            >
              Back to Sign In
            </Button>
          </Surface>
        </motion.div>
      </div>
    )
  }

  // Show email confirmation status
  if (confirmationStatus) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <Surface variant="default" className="rounded-2xl p-8 text-center">
            {confirmationStatus === 'checking' && (
              <>
                <LoadingSpinner className="w-16 h-16 mx-auto mb-4 text-accent" />
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Confirming Your Email...
                </h2>
                <p className="text-muted">
                  Please wait while we verify your email address.
                </p>
              </>
            )}

            {confirmationStatus === 'success' && (
              <>
                <div className="w-16 h-16 bg-success-soft rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-success-soft-foreground" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Email Confirmed!
                </h2>
                <p className="text-muted mb-4">
                  Your email has been successfully verified. You're now logged in and will be redirected to your dashboard.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-muted">
                  <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                  Redirecting to dashboard...
                </div>
              </>
            )}

            {confirmationStatus === 'error' && (
              <>
                <div className="w-16 h-16 bg-danger-soft rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-danger-soft-foreground" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Confirmation Failed
                </h2>
                <p className="text-muted mb-6">
                  We couldn't verify your email. The link may have expired or is invalid.
                </p>
                <Button
                  className="w-full"
                  onPress={() => {
                    setConfirmationStatus(null)
                    navigate('/auth')
                  }}
                >
                  Back to Sign In
                </Button>
              </>
            )}
          </Surface>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="relative z-10 w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="hidden lg:block"
        >
          <div className="flex flex-col items-start mb-8">
            {orgData?.branding?.logo_url ? (
              <img
                src={orgData.branding.logo_url}
                alt={`${appName} logo`}
                className="h-24 w-auto object-contain mb-4 max-w-xs"
              />
            ) : (
              <img
                src={theme === 'dark' ? '/icon-dark.svg' : '/icon-light.svg'}
                alt={appName}
                className="w-24 h-24 rounded-2xl mb-4"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-foreground">{appName}</h1>
              <p className="text-sm text-muted">{tagline}</p>
            </div>
          </div>

          <h2 className="text-4xl font-bold text-foreground mb-4">
            {isLogin ? 'Welcome back!' : 'Join our partner program'}
          </h2>
          <p className="text-lg text-muted">
            {isLogin
              ? 'Sign in to access your affiliate dashboard.'
              : 'Start earning by promoting our products and services.'}
          </p>
        </motion.div>

        {/* Right Side - Auth Form */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            {orgData?.branding?.logo_url ? (
              <img
                src={orgData.branding.logo_url}
                alt={`${appName} logo`}
                className="h-20 w-auto object-contain mb-4 max-w-[200px]"
              />
            ) : (
              <img
                src={theme === 'dark' ? '/icon-dark.svg' : '/icon-light.svg'}
                alt={appName}
                className="w-20 h-20 rounded-2xl mb-4"
              />
            )}
            <div className="text-center">
              <h1 className="text-xl font-bold text-foreground">{appName}</h1>
              <p className="text-xs text-muted">{tagline}</p>
            </div>
          </div>

          <Surface variant="default" className="rounded-2xl p-8">
            {/* Login Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-2">
                <LogIn className="h-7 w-7 text-accent" />
                <h2 className="text-2xl font-bold text-foreground">Sign In</h2>
              </div>
              <p className="text-muted">Access your affiliate dashboard</p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6"
              >
                <Alert status="danger" className="bg-danger-soft">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description className="text-danger-soft-foreground">{error}</Alert.Description>
                  </Alert.Content>
                </Alert>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <AnimatePresence mode="wait">
                {!isLogin && (
                  <motion.div
                    key="name"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <TextField
                      isRequired={!isLogin}
                      name="name"
                      value={formData.name}
                      onChange={(v) => setFormData(prev => ({ ...prev, name: v }))}
                    >
                      <Label>Full Name</Label>
                      <InputGroup variant="secondary">
                        <InputGroup.Prefix>
                          <UserIcon className="h-4 w-4" />
                        </InputGroup.Prefix>
                        <InputGroup.Input placeholder="John Doe" />
                      </InputGroup>
                    </TextField>
                  </motion.div>
                )}
              </AnimatePresence>

              <TextField
                isRequired
                type="email"
                name="email"
                value={formData.email}
                onChange={(v) => setFormData(prev => ({ ...prev, email: v }))}
              >
                <Label>Email Address</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Prefix>
                    <Mail className="h-4 w-4" />
                  </InputGroup.Prefix>
                  <InputGroup.Input placeholder="you@example.com" />
                </InputGroup>
              </TextField>

              <TextField
                isRequired
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={(v) => setFormData(prev => ({ ...prev, password: v }))}
              >
                <Label>Password</Label>
                <InputGroup variant="secondary">
                  <InputGroup.Prefix>
                    <Lock className="h-4 w-4" />
                  </InputGroup.Prefix>
                  <InputGroup.Input placeholder="••••••••" />
                  <InputGroup.Suffix>
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-muted hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
              </TextField>

              <AnimatePresence mode="wait">
                {!isLogin && (
                  <motion.div
                    key="referral"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <TextField
                      name="referredBy"
                      value={formData.referredBy}
                      onChange={(v) => setFormData(prev => ({ ...prev, referredBy: v }))}
                    >
                      <Label>Referral Code (Optional)</Label>
                      <InputGroup variant="secondary">
                        <InputGroup.Input placeholder="Enter referral code" />
                      </InputGroup>
                    </TextField>
                  </motion.div>
                )}
              </AnimatePresence>

              {isLogin && (
                <div className="flex items-center justify-between">
                  <Checkbox variant="secondary">
                    <Checkbox.Content className="gap-2">
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <span className="text-sm text-muted">Remember me</span>
                    </Checkbox.Content>
                  </Checkbox>
                  <button
                    type="button"
                    onClick={() => navigate('/auth/reset-password')}
                    className="text-sm font-medium text-accent hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <Button type="submit" className="w-full" isDisabled={loading}>
                {loading ? (
                  <LoadingSpinner className="h-4 w-4" />
                ) : (
                  <>
                    {isLogin ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted">
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                {isLogin ? (
                  <button
                    onClick={() => navigate('/auth/apply')}
                    className="font-medium text-accent hover:underline"
                  >
                    Apply to become an affiliate
                  </button>
                ) : (
                  <button
                    onClick={() => setIsLogin(true)}
                    className="font-medium text-accent hover:underline"
                  >
                    Sign in
                  </button>
                )}
              </p>
            </div>
          </Surface>
        </motion.div>
      </div>
    </div>
  )
}
